import { TableColumns } from '@bp/ui-components';
import { t } from 'i18next';
import type { WeekAvailability } from '../../../types/availability';
import { TimetableVersionTableType } from '../graphql/types';

export function createTimetableVersionTableColumns(name: string): TableColumns<TimetableVersionTableType>[] {
  return [
    {
      header: name,
      accessorKey: 'name',
      id: 'name',
      size: 150,
    },
    {
      header: t('common.shortName'),
      accessorKey: 'shortName',
      id: 'shortName',
      size: 90,
    },
    {
      header: t('timetableVersion.lessonCount', { context: 'short' }),
      meta: { tooltip: t('timetableVersion.lessonCount', { context: 'long' }) },
      accessorKey: 'count',
      id: 'count',
      size: 50,
    },
    {
      header: t('common.color'),
      accessorKey: 'color',
      id: 'color',
      type: 'color',
      size: 60,
    },
  ];
}

/** Fallback number of slots per day for availability bitstrings when the time grid length is unknown. */
export const DAY_SLOT_COUNT = 20;

/** Build an availability matrix where every day is `char` repeated `slotCount` times ('2' = free, '0' = blocked). */
export const buildAvailabilityMatrix = (char: '0' | '2', slotCount: number = DAY_SLOT_COUNT): WeekAvailability => {
  const row = char.repeat(slotCount);
  return { mon: row, tue: row, wed: row, thu: row, fri: row, sat: row, sun: row };
};

/** Pad every day to at least `slotCount` chars with free slots, so all time-grid slots stay editable. */
export const padAvailabilityMatrix = (matrix: WeekAvailability, slotCount: number): WeekAvailability => {
  const pad = (value?: string | null) => (value ?? '').padEnd(slotCount, '2');
  return {
    ...matrix,
    mon: pad(matrix.mon),
    tue: pad(matrix.tue),
    wed: pad(matrix.wed),
    thu: pad(matrix.thu),
    fri: pad(matrix.fri),
    sat: pad(matrix.sat),
    sun: pad(matrix.sun),
  };
};

export const initialMatrix: WeekAvailability = buildAvailabilityMatrix('2');
export const initialBlockedMatrix: WeekAvailability = buildAvailabilityMatrix('0');

/**
 * The editable availability matrix hides pause slots, so the strings it produces are "compact": one
 * bit per non-pause time-grid slot. The time grid (and everything indexing the bitstring by `order` —
 * the persisted edge properties and the conflict store) expects a slot for *every* entry, because
 * pauses still occupy an `order`. This expands a compact matrix to that full layout, placing each
 * non-pause bit at its `order` index and filling pause positions (and any order gaps) with '2' (free).
 *
 * Idempotent: a string that is already in full layout (length already covers the pause gaps) is read
 * by `order` and reproduced unchanged, so re-saving an already-expanded matrix does not shift bits.
 */
export const expandAvailabilityMatrixWithPauses = (
  matrix: WeekAvailability,
  timeGridEntries: { order?: number | null; pause?: boolean | null }[],
): WeekAvailability => {
  const nonPause = timeGridEntries.filter((entry) => !entry.pause).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const slotCount = timeGridEntries.reduce((max, entry) => Math.max(max, (entry.order ?? 0) + 1), 0);

  const expandDay = (value?: string | null): string => {
    const input = value ?? '';
    // Compact strings carry one bit per non-pause slot; an already-expanded string is longer (the
    // pause gaps are present) and must be read by `order` to stay idempotent.
    const isCompact = input.length <= nonPause.length;
    const slots = new Array(slotCount).fill('2');
    nonPause.forEach((entry, index) => {
      const order = entry.order ?? 0;
      slots[order] = (isCompact ? input[index] : input[order]) ?? '2';
    });
    return slots.join('');
  };

  return {
    ...matrix,
    mon: expandDay(matrix.mon),
    tue: expandDay(matrix.tue),
    wed: expandDay(matrix.wed),
    thu: expandDay(matrix.thu),
    fri: expandDay(matrix.fri),
    sat: expandDay(matrix.sat),
    sun: expandDay(matrix.sun),
  };
};
